import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { SvgIconComponent } from "@mui/icons-material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import type { NavItem } from "@/components/layout/Sidebar";
import { NavLinkRow } from "@/components/layout/SidebarNavRows";

const VIEWPORT_MARGIN = 12;
const FLYOUT_GAP = 6;
const FLYOUT_MIN_WIDTH = 208; // 13rem

type FlyoutCoords = {
  top: number;
  left: number;
  maxItemsHeight?: number;
};

type SidebarNavFlyoutProps = {
  label: string;
  icon: SvgIconComponent;
  items: NavItem[];
  accent?: "violet" | "slate";
  hasFeature?: (code: string) => boolean;
};

function clampFlyoutPosition(
  trigger: DOMRect,
  flyout: HTMLElement,
): FlyoutCoords {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let left = trigger.left + trigger.width + FLYOUT_GAP;
  if (left + FLYOUT_MIN_WIDTH > viewportW - VIEWPORT_MARGIN) {
    left = Math.max(
      VIEWPORT_MARGIN,
      trigger.left - FLYOUT_MIN_WIDTH - FLYOUT_GAP,
    );
  }

  const titleEl = flyout.querySelector(".workspace-nav-flyout-title");
  const itemsEl = flyout.querySelector(
    ".workspace-nav-flyout-items",
  ) as HTMLElement | null;
  const titleHeight = titleEl?.getBoundingClientRect().height ?? 36;
  const flyoutChrome = titleHeight + 16;
  const itemsHeight = itemsEl?.scrollHeight ?? 0;
  const idealHeight = flyoutChrome + itemsHeight;
  const maxPanelHeight = viewportH - VIEWPORT_MARGIN * 2;

  let top = trigger.top;
  let maxItemsHeight: number | undefined;

  if (idealHeight > maxPanelHeight) {
    top = VIEWPORT_MARGIN;
    maxItemsHeight = Math.max(96, maxPanelHeight - flyoutChrome);
  } else if (top + idealHeight > viewportH - VIEWPORT_MARGIN) {
    top = Math.max(VIEWPORT_MARGIN, viewportH - VIEWPORT_MARGIN - idealHeight);
  }

  return { top, left, maxItemsHeight };
}

export function SidebarNavFlyout({
  label,
  icon: Icon,
  items,
  hasFeature,
}: SidebarNavFlyoutProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<FlyoutCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const flyout = flyoutRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    if (flyout) {
      setCoords(clampFlyoutPosition(rect, flyout));
      return;
    }

    setCoords({
      top: Math.max(VIEWPORT_MARGIN, rect.top),
      left: rect.left + rect.width + FLYOUT_GAP,
    });
  }, []);

  const show = useCallback(() => {
    clearCloseTimer();
    updatePosition();
    setOpen(true);
  }, [clearCloseTimer, updatePosition]);

  const scheduleHide = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, items.length, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => updatePosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, updatePosition]);

  if (items.length === 0) return null;

  const flyout =
    open && coords
      ? createPortal(
          <div
            ref={flyoutRef}
            className="workspace-nav-flyout workspace-nav-flyout--fixed"
            style={{ top: coords.top, left: coords.left }}
            role="menu"
            aria-label={label}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
          >
            <p className="workspace-nav-flyout-title flex items-center gap-1.5">
              <Icon sx={{ fontSize: 14 }} className="opacity-80" />
              {label}
            </p>
            <div
              className="workspace-nav-flyout-items"
              style={
                coords.maxItemsHeight != null
                  ? { maxHeight: coords.maxItemsHeight }
                  : undefined
              }
            >
              {items.map((item) => (
                <NavLinkRow
                  key={item.to}
                  item={item}
                  collapsed={false}
                  locked={Boolean(
                    item.feature && hasFeature && !hasFeature(item.feature),
                  )}
                  variant="sub"
                  showIcon={false}
                  inFlyout
                />
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="workspace-nav-item w-full justify-center !px-2"
        aria-label={label}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        <Icon className="shrink-0 opacity-90" sx={{ fontSize: 20 }} />
      </button>
      {flyout}
    </>
  );
}

export function resolveGroupIcon(
  group: { id: string; items: NavItem[] },
  overrides?: Record<string, SvgIconComponent>,
): SvgIconComponent {
  if (overrides?.[group.id]) return overrides[group.id];
  return group.items[0]?.icon ?? FolderOutlinedIcon;
}
